import { CompileError } from '../types/errors';
import { friendlyName, isCustomBinDimension, isNonAggregateMetric, isPostCalculationMetric, } from '../types/field';
import { dateGranularityToTimeFrameMap, } from '../types/timeFrames';
import { timeFrameConfigs } from '../utils/timeFrames';
import { expandFieldsWithSets } from './fieldSetExpander';
import { renderFilterRuleSqlFromField } from './filtersCompiler';
import { getCategoriesFromResource, getSpotlightConfigurationForResource, } from './lightdashProjectConfig';
import { getAvailableParameterNames, getAvailableParametersFromTables, getParameterReferences, validateParameterNames, validateParameterReferences, } from './parameters';
// exclude lightdash prefix from variable pattern
export const lightdashVariablePattern = /\$\{((?!(lightdash|ld)\.)[a-zA-Z0-9_.]+)\}/g;
export const getParsedReference = (ref, currentTable) => {
    // Reference to another dimension
    const split = ref.split('.');
    if (split.length > 2) {
        throw new CompileError(`Model "${currentTable}" cannot resolve dimension reference: \${${ref}}`, {});
    }
    const refTable = split.length === 1 ? currentTable : split[0];
    const refName = split.length === 1 ? split[0] : split[1];
    return { refTable, refName };
};
export const getAllReferences = (raw) => (raw.match(lightdashVariablePattern) || []).map((value) => value.slice(2, value.length - 1));
export const parseAllReferences = (raw, currentTable) => getAllReferences(raw).map((ref) => getParsedReference(ref, currentTable));
const getReferencedTable = (refTable, tables) => {
    if (tables[refTable]) {
        return tables[refTable];
    }
    return Object.values(tables).find((table) => table.name === refTable || table.originalName === refTable);
};
export class ExploreCompiler {
    constructor(warehouseClient) {
        this.warehouseClient = warehouseClient;
    }
    compileExplore({ name, label, tags, baseTable, joinedTables, tables, targetDatabase, groupLabel, warehouse, ymlPath, sqlPath, spotlightConfig, meta, databricksCompute, aiHint, projectParameters, }) {
        // Check that base table and joined tables exist
        if (!tables[baseTable]) {
            throw new CompileError(`Failed to compile explore "${name}". Tried to find base table but cannot find table with name "${baseTable}"`, {});
        }
        joinedTables.forEach((join) => {
            if (!tables[join.table]) {
                throw new CompileError(`Failed to compile explore "${name}". Tried to join table "${join.table}" to "${baseTable}" but cannot find table with name "${join.table}"`, {});
            }
        });
        const aliases = [
            baseTable,
            ...joinedTables.map((join) => join.alias || join.table),
        ];
        if (aliases.length !== new Set(aliases).size) {
            throw new CompileError(`Failed to compile explore "${name}". Cannot join to the same table multiple times table in an explore. Use an 'alias'`, {});
        }
        const { isInvalid: hasInvalidParameterNames, invalidParameters } = validateParameterNames(meta.parameters);
        if (hasInvalidParameterNames) {
            throw new CompileError(`Failed to compile explore "${name}". Invalid parameter names: ${invalidParameters.join(', ')}`, {
                invalidParameters,
            });
        }
        const includedTables = joinedTables.reduce((prev, join) => {
            const joinTableName = join.alias || tables[join.table].name;
            const joinTableLabel = join.label ||
                (join.alias && friendlyName(join.alias)) ||
                tables[join.table].label;
            // Expand field sets if join.fields contains set references
            let expandedFields;
            if (join.fields) {
                expandedFields = expandFieldsWithSets(join.fields, tables[join.table]);
            }
            const requiredDimensionsForJoin = parseAllReferences(join.sqlOn, join.table).reduce((acc, reference) => {
                if (reference.refTable === joinTableName) {
                    acc.push(reference.refName);
                }
                return acc;
            }, []);
            const tableDimensions = tables[join.table].dimensions;
            return {
                ...prev,
                [join.alias || join.table]: {
                    ...tables[join.table],
                    originalName: tables[join.table].name,
                    name: joinTableName,
                    label: joinTableLabel,
                    hidden: join.hidden,
                    dimensions: Object.keys(tableDimensions).reduce((acc, dimensionKey) => {
                        const dimension = tableDimensions[dimensionKey];
                        const isRequired = requiredDimensionsForJoin.includes(dimensionKey);
                        const isTimeIntervalBaseDimensionVisible = dimension.timeInterval &&
                            dimension.timeIntervalBaseDimensionName &&
                            expandedFields
                            ? expandedFields.includes(dimension.timeIntervalBaseDimensionName)
                            : false;
                        const isVisible = expandedFields === undefined ||
                            expandedFields.includes(dimensionKey) ||
                            (dimension.group !== undefined &&
                                expandedFields.includes(dimension.group)) ||
                            isTimeIntervalBaseDimensionVisible;
                        if (isRequired || isVisible) {
                            acc[dimensionKey] = {
                                ...dimension,
                                hidden: join.hidden ||
                                    dimension.hidden ||
                                    !isVisible,
                                table: joinTableName,
                                tableLabel: joinTableLabel,
                            };
                        }
                        return acc;
                    }, {}),
                    metrics: Object.keys(tables[join.table].metrics)
                        .filter((d) => expandedFields === undefined ||
                        expandedFields.includes(d))
                        .reduce((prevMetrics, metricKey) => {
                        const metric = tables[join.table].metrics[metricKey];
                        return {
                            ...prevMetrics,
                            [metricKey]: {
                                ...metric,
                                hidden: !!join.hidden || metric.hidden,
                                table: joinTableName,
                                tableLabel: joinTableLabel,
                            },
                        };
                    }, {}),
                },
            };
        }, { [baseTable]: tables[baseTable] });
        // get all available parameters from the included tables
        const exploreAvailableParameters = getAvailableParametersFromTables(Object.values(includedTables));
        // available parameters are the combination of project parameters and model parameters
        // used for validating parameter references
        const availableParametersNames = getAvailableParameterNames(projectParameters, exploreAvailableParameters);
        const compiledTables = aliases.reduce((prev, tableName) => ({
            ...prev,
            [tableName]: this.compileTable(includedTables[tableName], includedTables, availableParametersNames),
        }), {});
        const compiledJoins = joinedTables.map((j) => this.compileJoin(j, includedTables, availableParametersNames));
        const spotlightVisibility = meta.spotlight?.visibility ?? spotlightConfig?.default_visibility;
        const spotlightCategories = getCategoriesFromResource('explore', name, spotlightConfig, meta.spotlight?.categories);
        return {
            name,
            label,
            tags,
            baseTable,
            joinedTables: compiledJoins,
            tables: compiledTables,
            targetDatabase,
            groupLabel,
            warehouse,
            ymlPath,
            sqlPath,
            databricksCompute,
            ...(aiHint ? { aiHint } : {}),
            ...getSpotlightConfigurationForResource(spotlightVisibility, spotlightCategories),
            ...(meta.parameters && Object.keys(meta.parameters).length > 0
                ? { parameters: meta.parameters }
                : {}),
        };
    }
    compileTable(table, tables, availableParameters) {
        const dimensions = Object.keys(table.dimensions).reduce((prev, dimensionKey) => ({
            ...prev,
            [dimensionKey]: this.compileDimension(table.dimensions[dimensionKey], tables, availableParameters),
        }), {});
        const metrics = Object.keys(table.metrics).reduce((prev, metricKey) => ({
            ...prev,
            [metricKey]: this.compileMetric(table.metrics[metricKey], tables, availableParameters),
        }), {});
        const compiledSqlWhere = table.sqlWhere?.replace(lightdashVariablePattern, (_, p1) => {
            const compiledReference = this.compileDimensionReference(p1, tables, table.name);
            return compiledReference.sql;
        });
        // Extract parameter references from sqlWhere
        const parameterReferences = compiledSqlWhere
            ? getParameterReferences(compiledSqlWhere)
            : [];
        validateParameterReferences(table.name, parameterReferences, availableParameters);
        return {
            ...table,
            uncompiledSqlWhere: table.sqlWhere,
            sqlWhere: compiledSqlWhere,
            dimensions,
            metrics,
            ...(parameterReferences.length > 0 ? { parameterReferences } : {}),
            ...(table.parameters && Object.keys(table.parameters).length > 0
                ? { parameters: table.parameters }
                : {}),
        };
    }
    static expandShowUnderlyingValueSets(metric, tables) {
        if (!Array.isArray(metric.showUnderlyingValues)) {
            return undefined;
        }
        const currentTable = tables[metric.table];
        const containsSetFields = metric.showUnderlyingValues.some((field) => field.endsWith('*'));
        if (!containsSetFields || !currentTable) {
            return metric.showUnderlyingValues;
        }
        const expandedValues = expandFieldsWithSets([...metric.showUnderlyingValues], currentTable);
        expandedValues.forEach((fieldRef) => {
            const { refTable, refName } = getParsedReference(fieldRef, metric.table);
            const referencedTable = getReferencedTable(refTable, tables);
            const isValidReference = !!(referencedTable?.dimensions[refName] ||
                referencedTable?.metrics[refName]);
            if (!isValidReference) {
                throw new CompileError(`"show_underlying_values" for metric "${metric.name}" has a reference to an unknown field: ${fieldRef} in table "${metric.table}"`);
            }
        });
        return expandedValues;
    }
    compileMetric(metric, tables, availableParameters) {
        const compiledMetric = this.compileMetricSql(metric, tables, availableParameters);
        const showUnderlyingValues = ExploreCompiler.expandShowUnderlyingValueSets(metric, tables);
        const tablesRequiredAttributes = Array.from(compiledMetric.tablesReferences).reduce((acc, tableReference) => {
            const table = tables[tableReference];
            if (table?.requiredAttributes) {
                acc[tableReference] = table.requiredAttributes;
            }
            return acc;
        }, {});
        const compiledSql = compiledMetric.sql;
        // Extract parameter references from metric sql
        const parameterReferences = getParameterReferences(compiledSql);
        validateParameterReferences(metric.table, parameterReferences, availableParameters);
        return {
            ...metric,
            compiledSql,
            showUnderlyingValues,
            tablesReferences: Array.from(compiledMetric.tablesReferences),
            ...(Object.keys(tablesRequiredAttributes).length
                ? { tablesRequiredAttributes }
                : {}),
            ...(parameterReferences.length > 0 ? { parameterReferences } : {}),
        };
    }
    compileMetricSql(metric, tables, availableParameters) {
        // Metric might have references to other dimensions
        if (!tables[metric.table]) {
            throw new CompileError(`Metric "${metric.name}" references a table "${metric.table}" which matches no model`, {});
        }
        const currentRef = `${metric.table}.${metric.name}`;
        const currentShortRef = metric.name;
        let tablesReferences = new Set([metric.table]);
        let renderedSql = metric.sql.replace(lightdashVariablePattern, (_, p1) => {
            if ([currentShortRef, currentRef].includes(p1)) {
                throw new CompileError(`Metric "${metric.name}" in table "${metric.table}" has a sql string referencing itself: "${metric.sql}"`, {});
            }
            const compiledReference = isNonAggregateMetric(metric) ||
                isPostCalculationMetric(metric)
                ? this.compileMetricReference(p1, tables, metric.table, availableParameters)
                : this.compileDimensionReference(p1, tables, metric.table);
            tablesReferences = new Set([
                ...tablesReferences,
                ...compiledReference.tablesReferences,
            ]);
            return compiledReference.sql;
        });
        if (metric.filters !== undefined && metric.filters.length > 0) {
            if (isNonAggregateMetric(metric) ||
                isPostCalculationMetric(metric)) {
                throw new CompileError(`Error: ${metric.name} - metric filters cannot be used with non-aggregate or post-calculation metrics`);
            }
            const conditions = metric.filters.map((filter) => {
                const fieldRef = 
                // @ts-expect-error This fallback is to support old metric filters in yml. We can delete this after a few months since we can assume all projects have been redeployed
                filter.target.fieldRef || filter.target.fieldId;
                const { refTable, refName } = getParsedReference(fieldRef, metric.table);
                const table = tables[refTable];
                if (!table) {
                    throw new CompileError(`Filter for metric "${metric.name}" has a reference to an unknown table`);
                }
                // NOTE: date dimensions from explores have their time format uppercased (e.g. order_date_DAY) - see ticket: https://github.com/lightdash/lightdash/issues/5998
                const dimensionRefName = Object.keys(table.dimensions).find((key) => key.toLowerCase() === refName.toLowerCase());
                const dimensionField = dimensionRefName
                    ? table.dimensions[dimensionRefName]
                    : undefined;
                if (!dimensionField) {
                    throw new CompileError(`Filter for metric "${metric.name}" has a reference to an unknown dimension: ${fieldRef}`);
                }
                const compiledDimension = this.compileDimension(dimensionField, tables, availableParameters);
                if (compiledDimension.tablesReferences) {
                    tablesReferences = new Set([
                        ...tablesReferences,
                        ...compiledDimension.tablesReferences,
                    ]);
                }
                return renderFilterRuleSqlFromField(filter, compiledDimension, this.warehouseClient.getFieldQuoteChar(), this.warehouseClient.getStringQuoteChar(), this.warehouseClient.escapeString.bind(this.warehouseClient), this.warehouseClient.getStartOfWeek(), this.warehouseClient.getAdapterType());
            });
            renderedSql = `CASE WHEN (${conditions.join(' AND ')}) THEN (${renderedSql}) ELSE NULL END`;
        }
        const compiledSql = this.warehouseClient.getMetricSql(renderedSql, metric);
        return { sql: compiledSql, tablesReferences };
    }
    compileDimension(dimension, tables, availableParameters) {
        const compiledDimension = this.compileDimensionSql(dimension, tables);
        const tablesRequiredAttributes = Array.from(compiledDimension.tablesReferences).reduce((acc, tableReference) => {
            const table = tables[tableReference];
            if (table?.requiredAttributes) {
                acc[tableReference] = table.requiredAttributes;
            }
            return acc;
        }, {});
        const compiledSql = compiledDimension.sql;
        // Extract parameter references from dimension sql
        const parameterReferences = getParameterReferences(compiledSql);
        validateParameterReferences(dimension.table, parameterReferences, availableParameters);
        return {
            ...dimension,
            compiledSql,
            tablesReferences: Array.from(compiledDimension.tablesReferences),
            ...(Object.keys(tablesRequiredAttributes).length
                ? { tablesRequiredAttributes }
                : {}),
            ...(parameterReferences.length > 0 ? { parameterReferences } : {}),
        };
    }
    compileDimensionSql(dimension, tables) {
        // Dimension might have references to other dimensions
        // Check we don't reference yourself
        const currentRef = `${dimension.table}.${dimension.name}`;
        const currentShortRef = dimension.name;
        let tablesReferences = new Set([dimension.table]);
        const sql = dimension.sql.replace(lightdashVariablePattern, (_, p1) => {
            if ([currentShortRef, currentRef].includes(p1)) {
                throw new CompileError(`Dimension "${dimension.name}" in table "${dimension.table}" has a sql string referencing itself: "${dimension.sql}"`, {});
            }
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const compiledReference = this.compileDimensionReference(p1, tables, dimension.table);
            tablesReferences = new Set([
                ...tablesReferences,
                ...compiledReference.tablesReferences,
            ]);
            return compiledReference.sql;
        });
        return { sql, tablesReferences };
    }
    compileCustomDimensionSql(dimension, tables, availableParameters) {
        const currentRef = dimension.id;
        let tablesReferences = new Set([]);
        const compiledSql = dimension.sql.replace(lightdashVariablePattern, (_, p1) => {
            if (currentRef === p1) {
                throw new CompileError(`Dimension "${dimension.name}" in table "${dimension.table}" has a sql string referencing itself: "${dimension.sql}"`, {});
            }
            const compiledReference = this.compileDimensionReference(p1, tables, dimension.table);
            tablesReferences = new Set([
                ...tablesReferences,
                ...compiledReference.tablesReferences,
            ]);
            return compiledReference.sql;
        });
        // Extract parameter references from custom dimension sql
        const parameterReferences = getParameterReferences(compiledSql);
        validateParameterReferences(dimension.table, parameterReferences, availableParameters);
        return {
            compiledSql,
            tablesReferences: Array.from(tablesReferences),
            ...(parameterReferences.length > 0 ? { parameterReferences } : {}),
        };
    }
    compileCustomDimension(customDimension, tables, availableParameters) {
        if (isCustomBinDimension(customDimension)) {
            return customDimension;
        }
        const compiledCustomDimensionSql = this.compileCustomDimensionSql(customDimension, tables, availableParameters);
        return {
            ...customDimension,
            ...compiledCustomDimensionSql,
        };
    }
    compileDimensionReference(ref, tables, currentTable) {
        // Reference to current table
        if (ref === 'TABLE') {
            const fieldQuoteChar = this.warehouseClient.getFieldQuoteChar();
            return {
                sql: `${fieldQuoteChar}${currentTable}${fieldQuoteChar}`,
                tablesReferences: new Set([currentTable]),
            };
        }
        const { refTable, refName } = getParsedReference(ref, currentTable);
        /** Resolve the table reference through its original name, or via an alias: */
        const referencedTable = getReferencedTable(refTable, tables);
        const referencedDimension = referencedTable?.dimensions[refName];
        if (referencedDimension === undefined) {
            throw new CompileError(`Model "${currentTable}" has a dimension reference: \${${ref}} which matches no dimension`, {});
        }
        const compiledDimension = this.compileDimensionSql(referencedDimension, tables);
        return {
            sql: `(${compiledDimension.sql})`,
            tablesReferences: new Set([
                referencedTable?.name || refTable,
                ...compiledDimension.tablesReferences,
            ]),
        };
    }
    compileMetricReference(ref, tables, currentTable, availableParameters) {
        // Reference to current table
        if (ref === 'TABLE') {
            const fieldQuoteChar = this.warehouseClient.getFieldQuoteChar();
            return {
                sql: `${fieldQuoteChar}${currentTable}${fieldQuoteChar}`,
                tablesReferences: new Set([currentTable]),
            };
        }
        const { refTable: refTableName, refName } = getParsedReference(ref, currentTable);
        const referencedTable = getReferencedTable(refTableName, tables);
        const referencedMetric = referencedTable?.metrics[refName];
        if (referencedMetric === undefined) {
            throw new CompileError(`Model "${currentTable}" has a metric reference: \${${ref}} which matches no metric`, {});
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const compiledMetric = this.compileMetricSql(referencedMetric, tables, availableParameters);
        return {
            sql: `(${compiledMetric.sql})`,
            tablesReferences: new Set([
                referencedTable?.name || refTableName,
                ...compiledMetric.tablesReferences,
            ]),
        };
    }
    compileExploreJoinSql(join, tables) {
        const tablesReferences = new Set([]);
        // Sql join contains references to dimensions
        const sql = join.sqlOn.replace(lightdashVariablePattern, (_, p1) => {
            const compiledReference = this.compileDimensionReference(p1, tables, join.table);
            // Update table references
            compiledReference.tablesReferences.forEach((value) => tablesReferences.add(value));
            return compiledReference.sql;
        });
        return { sql, tablesReferences };
    }
    compileJoin(join, tables, availableParameters) {
        const { sql, tablesReferences } = this.compileExploreJoinSql({
            table: join.alias || join.table,
            sqlOn: join.sqlOn,
        }, tables);
        // Extract parameter references from sqlOn
        const parameterReferences = getParameterReferences(sql);
        validateParameterReferences(join.table, parameterReferences, availableParameters);
        return {
            table: join.alias || join.table,
            sqlOn: join.sqlOn,
            type: join.type,
            compiledSqlOn: sql,
            tablesReferences: Array.from(tablesReferences),
            hidden: join.hidden,
            always: join.always,
            relationship: join.relationship,
            ...(parameterReferences.length > 0 ? { parameterReferences } : {}),
        };
    }
}
export const createDimensionWithGranularity = (dimensionName, baseTimeDimension, explore, warehouseSqlBuilder, granularity, availableParameters) => {
    const newTimeInterval = dateGranularityToTimeFrameMap[granularity];
    const exploreCompiler = new ExploreCompiler(warehouseSqlBuilder);
    return exploreCompiler.compileDimension({
        ...baseTimeDimension,
        name: dimensionName,
        type: timeFrameConfigs[newTimeInterval].getDimensionType(baseTimeDimension.type),
        timeInterval: newTimeInterval,
        label: `${baseTimeDimension.label} ${timeFrameConfigs[newTimeInterval]
            .getLabel()
            .toLowerCase()}`,
        sql: timeFrameConfigs[newTimeInterval].getSql(warehouseSqlBuilder.getAdapterType(), newTimeInterval, baseTimeDimension.sql, timeFrameConfigs[newTimeInterval].getDimensionType(baseTimeDimension.type), warehouseSqlBuilder.getStartOfWeek()),
    }, explore.tables, availableParameters);
};
//# sourceMappingURL=exploreCompiler.js.map